import { claimTariffStatusEnumValues } from '@billing/validation/tariff';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

export const ALL_CLAIM_TAB = 'all' as const;

export const CLERK_CLAIM_TABS = [
  ALL_CLAIM_TAB,
  'actionNeeded',
  'preSubmission',
  'submittedToPayor',
  'underBillerReview',
  'underPayorReview',
  'invoiced',
  'completed',
] as const;

export const PROVIDER_CLAIM_TABS = [
  'preSubmission',
  'outstanding',
  'completed',
  ALL_CLAIM_TAB,
] as const;

export const CLAIM_TABS = [
  ...CLERK_CLAIM_TABS,
  ...PROVIDER_CLAIM_TABS,
] as const;

export type ClerkClaimTabKey = (typeof CLERK_CLAIM_TABS)[number];
export type ProviderClaimTabKey = (typeof PROVIDER_CLAIM_TABS)[number];
export type ClaimTabKey = (typeof CLAIM_TABS)[number];
export type TariffRowFilterMode = 'showAllTariffs' | 'showMatchingTariffsOnly';
export const DEFAULT_CLAIM_TAB: ClerkClaimTabKey = 'preSubmission';
export const DEFAULT_PROVIDER_CLAIM_TAB: ProviderClaimTabKey = 'preSubmission';
export const DEFAULT_TARIFF_ROW_FILTER_MODE: TariffRowFilterMode =
  'showMatchingTariffsOnly';

interface ClaimTabFilterState {
  activeTab: ClaimTabKey;
  selectedTariffStatuses: string[];
  providerNeedsAction: boolean;
  tariffRowFilterMode: TariffRowFilterMode;
  setActiveTab: (tab: ClaimTabKey) => void;
  setRoleDefaultTab: (isProvider: boolean) => void;
  toggleSubStatus: (status: string) => void;
  toggleProviderNeedsAction: () => void;
  toggleTariffRowFilterMode: () => void;
  clearSubStatuses: () => void;
  resetClaimTabFilters: (isProvider?: boolean) => void;
}

const sanitizeSubStatuses = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];

const normalizeSubStatuses = (
  value: unknown,
  preserveExplicitStatuses = false
): string[] => {
  const statuses = sanitizeSubStatuses(value);
  if (
    preserveExplicitStatuses &&
    statuses.every((status) =>
      claimTariffStatusEnumValues.some((candidate) => candidate === status)
    )
  ) {
    return [...new Set(statuses)];
  }
  const lastSelected = statuses[statuses.length - 1];
  return lastSelected ? [lastSelected] : [];
};

const sanitizeActiveTab = (value: unknown): ClaimTabKey => {
  if (value === 'inProgress') return DEFAULT_PROVIDER_CLAIM_TAB;
  return CLAIM_TABS.includes(value as ClaimTabKey)
    ? (value as ClaimTabKey)
    : DEFAULT_CLAIM_TAB;
};

export const isProviderClaimTab = (
  value: ClaimTabKey
): value is ProviderClaimTabKey =>
  PROVIDER_CLAIM_TABS.includes(value as ProviderClaimTabKey);

export const isClerkClaimTab = (
  value: ClaimTabKey
): value is ClerkClaimTabKey =>
  CLERK_CLAIM_TABS.includes(value as ClerkClaimTabKey);

export const useClaimTabFilterStore = create<ClaimTabFilterState>()(
  devtools(
    persist(
      (set) => ({
        activeTab: DEFAULT_CLAIM_TAB,
        selectedTariffStatuses: [],
        providerNeedsAction: false,
        tariffRowFilterMode: DEFAULT_TARIFF_ROW_FILTER_MODE,
        setActiveTab: (tab) =>
          set((state) => {
            if (state.activeTab === tab) return state;
            return {
              activeTab: tab,
              selectedTariffStatuses: [],
            };
          }),
        setRoleDefaultTab: (isProvider) =>
          set((state) => {
            const defaultTab = isProvider
              ? DEFAULT_PROVIDER_CLAIM_TAB
              : DEFAULT_CLAIM_TAB;
            if (state.activeTab === defaultTab) return state;
            return {
              activeTab: defaultTab,
              selectedTariffStatuses: [],
            };
          }),
        toggleSubStatus: (status) =>
          set((state) => {
            const selectedTariffStatuses = normalizeSubStatuses(
              state.selectedTariffStatuses
            );
            const exists = selectedTariffStatuses[0] === status;
            return {
              selectedTariffStatuses: exists ? [] : [status],
            };
          }),
        toggleProviderNeedsAction: () =>
          set((state) => ({
            providerNeedsAction: !state.providerNeedsAction,
          })),
        toggleTariffRowFilterMode: () =>
          set((state) => ({
            tariffRowFilterMode:
              state.tariffRowFilterMode === 'showMatchingTariffsOnly'
                ? 'showAllTariffs'
                : 'showMatchingTariffsOnly',
          })),
        clearSubStatuses: () => set({ selectedTariffStatuses: [] }),
        resetClaimTabFilters: (isProvider = false) =>
          set({
            activeTab: isProvider
              ? DEFAULT_PROVIDER_CLAIM_TAB
              : DEFAULT_CLAIM_TAB,
            selectedTariffStatuses: [],
            providerNeedsAction: false,
            tariffRowFilterMode: DEFAULT_TARIFF_ROW_FILTER_MODE,
          }),
      }),
      {
        name: 'claim-tab-filter-storage',
        storage: createJSONStorage(() => localStorage),
        merge: (persistedState, currentState) => {
          const nextState =
            persistedState as Partial<ClaimTabFilterState> | null;
          if (!nextState) return currentState;
          const selectedTariffStatuses = normalizeSubStatuses(
            nextState.selectedTariffStatuses ??
              (persistedState as { selectedSubStatuses?: unknown } | null)
                ?.selectedSubStatuses,
            nextState.activeTab === ALL_CLAIM_TAB
          );
          return {
            ...currentState,
            ...nextState,
            activeTab: sanitizeActiveTab(nextState.activeTab),
            selectedTariffStatuses,
            providerNeedsAction: nextState.providerNeedsAction === true,
            tariffRowFilterMode:
              nextState.tariffRowFilterMode === 'showAllTariffs'
                ? 'showAllTariffs'
                : DEFAULT_TARIFF_ROW_FILTER_MODE,
          };
        },
      }
    ),
    {
      name: 'ClaimTabFilterStore',
      store: 'ClaimTabFilterStore',
      anonymousActionType: 'action',
      enabled: true,
    }
  )
);
